import { createContext, useCallback, useContext, useMemo, useState, ReactNode } from 'react';
import { useGetProcedureCodes, useGetServices } from '../api/services/index';
import {
  type ParsedServiceDetails,
  type GetSchedulingDescriptorsResponse,
  appointmentTypeToServiceDetails,
} from '../api/services/transformers';
import { FormStateProvider } from '../components/form-state-context';
import { validateDatetimeValue } from '../utils/utilities';

interface ServicesDataContextValue {
  parsedServices: ParsedServiceDetails[];
  procedureCodesData: GetSchedulingDescriptorsResponse | undefined;
  isLoading: boolean;
  refetchServicesData: () => void;
  refetchProcedureCodesData: () => void;
  lastSyncedAt: Date | null;
  hasUnpublishedChanges: boolean;
  markAsPublished: () => void;
  setHasUnpublishedChanges: (value: boolean) => void;
}

const ServicesDataContext = createContext<ServicesDataContextValue | null>(null);

interface ServicesDataProviderProps {
  children: ReactNode;
  locationId: string;
  orgId: string;
  enabled?: boolean;
}

export const ServicesDataProvider = ({ children, locationId, orgId, enabled = true }: ServicesDataProviderProps) => {
  const [hasUnpublishedChanges, setHasUnpublishedChanges] = useState(false);
  const markAsPublished = useCallback(() => setHasUnpublishedChanges(false), []);

  const { servicesData, isFetchingServicesData, refetchServicesData } = useGetServices({
    locationId,
    orgId,
    enabled,
  });

  const { procedureCodesData, isFetchingProcedureCodesData, refetchProcedureCodesData } = useGetProcedureCodes({
    locationId,
    orgId,
    enabled,
  });

  const parsedServices = useMemo<ParsedServiceDetails[]>(() => {
    if (!servicesData?.appointmentTypes) return [];
    return servicesData.appointmentTypes.map(appointmentTypeToServiceDetails);
  }, [servicesData]);

  const isLoading = isFetchingServicesData || isFetchingProcedureCodesData;
  const lastSyncedAt = validateDatetimeValue(servicesData?.lastSyncedAt || procedureCodesData?.lastSyncedAt);

  return (
    <ServicesDataContext.Provider
      value={{
        parsedServices,
        procedureCodesData,
        isLoading,
        refetchServicesData,
        refetchProcedureCodesData,
        lastSyncedAt,
        hasUnpublishedChanges,
        setHasUnpublishedChanges,
        markAsPublished,
      }}
    >
      <FormStateProvider>{children}</FormStateProvider>
    </ServicesDataContext.Provider>
  );
};

export const useServicesData = (): ServicesDataContextValue => {
  const context = useContext(ServicesDataContext);
  if (!context) {
    throw new Error('useServicesData must be used within a ServicesDataProvider');
  }
  return context;
};
